import { Button } from '@neo/index'
import { Typography } from '@neo/primitives/typography'
import { useNavigate } from 'router-v6'
/**
 * Why a page is closed to this user. Lives here, next to the copy it selects,
 * so the dependency runs hook -> component in one direction only.
 *
 * `unknown` is the honest default for a route-level gate. A route's
 * `notAllowed` is one boolean over several different conditions (role, plan,
 * region, feature flag, and combinations), so anything more specific would be a
 * guess: `/compliance` is region-gated, `/teams` can be plan-gated. A caller
 * that genuinely knows which gate fired passes the precise reason instead.
 */
export type AccessDenialReason = 'unknown' | 'role' | 'plan' | 'region' | 'feature'

// What to say for each gate. Kept deliberately plain: the user can't act on the
// permission model, only on who to ask or what to upgrade.
const COPY: Record<AccessDenialReason, { title: string; body: string }> = {
  unknown: {
    title: "You don't have access to this page",
    body: "Access here depends on your role, your plan, and your account's region. An account administrator can tell you which applies and make the change if it's available to you.",
  },
  role: {
    title: "You don't have access to this page",
    body: 'Your role on this account does not include this page. An account administrator can give you access or make the change for you.',
  },
  plan: {
    title: 'This page needs a different plan',
    body: 'This feature is not included in your current plan. An account administrator can upgrade to unlock it.',
  },
  region: {
    title: 'This page is not available in your region',
    body: "This feature isn't offered for your account's data region. A region cannot be changed after signup, so a separate organization would be needed.",
  },
  feature: {
    title: 'This page is not enabled for your account',
    body: 'This feature is not turned on for your organization. An account administrator can request it from Plivo support.',
  },
}

interface AccessDeniedProps {
  reason?: AccessDenialReason
}

/**
 * Shown when a page exists but the signed-in user may not open it.
 *
 * Distinct from `NotFoundPage` on purpose: a 404 tells the user the page is
 * gone and sends them looking for something that was never missing (Pylon
 * #7741, SER-6206). Entity routes that must not confirm an id exists on
 * another account still use the 404 — see the hook's docs.
 *
 * Deliberately imports nothing from `v2/configs/routes`. This renders from
 * `generateRoutes` in `v2/utils`, and `configs/routes` imports `v2/utils`, so
 * reaching for `getDefaultRoute()` here closes a require cycle and every module
 * in it dies with "Cannot access 'GLOBAL_API_URL' before initialization". Home
 * is `/`, whose index route already redirects to the default route.
 */
export const AccessDenied = ({ reason = 'unknown' }: AccessDeniedProps) => {
  const navigate = useNavigate()
  const { title, body } = COPY[reason]

  return (
    <div
      className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center animate-in fade-in duration-500"
      data-testid="access-denied-page"
    >
      <Typography variant="H1/500" className="text-2xl">
        {title}
      </Typography>
      <Typography variant="P/400" className="max-w-md text-secondary">
        {body}
      </Typography>
      <Button
        size="sm"
        className="mt-2"
        data-testid="access-denied-home-btn"
        onClick={() => navigate('/')}
      >
        Take me home
      </Button>
    </div>
  )
}
